import React, { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TabGroup, TabItem, TabGroupConfig } from '@/components/ui/TabGroup';
import { Button } from '@/components/ui/Button';

type MediaType = 'images' | 'videos';

interface EditorLibraryLayoutProps {
  activeMediaType: MediaType;
  children: ReactNode;
  onEnterReviewMode?: () => void;
  hasItems?: boolean;
}

export const EditorLibraryLayout: React.FC<EditorLibraryLayoutProps> = ({
  activeMediaType,
  children,
  onEnterReviewMode,
  hasItems = false,
}) => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();

  const mediaTypeTabs: TabItem<MediaType>[] = [
    {
      id: 'images',
      label: 'Images',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      id: 'videos',
      label: 'Videos',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
    },
  ];

  const tabConfig: TabGroupConfig<MediaType> = {
    variant: 'navigation',
    size: 'md',
    layout: 'horizontal',
    hasBackground: true,
  };

  const handleTabChange = (mediaType: MediaType) => {
    navigate(`/editor/${clientId}/library/${mediaType}`);
  };

  return (
    <div className="h-full px-4 lg:px-6 py-4 grid gap-4" style={{ gridTemplateRows: 'auto 1fr' }}>
      {/* Header with tabs */}
      <div className="flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="inline-block">
            <TabGroup
              items={mediaTypeTabs}
              activeId={activeMediaType}
              onTabChange={handleTabChange}
              config={tabConfig}
            />
          </div>

          {onEnterReviewMode && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onEnterReviewMode}
              disabled={!hasItems}
            >
              Enter Review Mode
            </Button>
          )}
        </div>
      </div>

      {/* Content - fills remaining space */}
      <div className="min-h-0 overflow-hidden">
        {children}
      </div>
    </div>
  );
};
